import { type ReminderDto } from '../../services/api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { esc, formatDate, formatRecurrence, getDaysUntil, isDueSoon, isOverdue, type ReminderFilterStatus } from './reminder-shared';

export class RcReminderListView extends HTMLElement {
  reminders: ReminderDto[] = [];
  filteredReminders: ReminderDto[] = [];
  filterStatus: ReminderFilterStatus = 'open';

  render(): void {
    const openCount = this.reminders.filter((reminder) => !reminder.isCompleted).length;
    const overdueCount = this.reminders.filter((reminder) => !reminder.isCompleted && isOverdue(reminder.dueDate)).length;
    const docs = this.filteredReminders;

    this.innerHTML = `
      <div class="page-header">
        <h1>${t('reminder.title')}</h1>
        <button class="btn btn-primary" data-action="add">${icon('plus', { size: 16, strokeWidth: 2.5 })}${t('reminder.add')}</button>
      </div>
      ${overdueCount > 0 ? `<div class="alert alert-danger">${icon('alert-circle', { size: 16 })}<strong>${t('reminder.overdueAlert', { count: overdueCount, suffix: overdueCount !== 1 ? 's' : '' })}</strong></div>` : ''}
      <div class="filter-bar">
        <button class="filter-btn ${this.filterStatus === 'open' ? 'active' : ''}" data-filter="open">${t('common.open')} (${openCount})</button>
        <button class="filter-btn ${this.filterStatus === 'all' ? 'active' : ''}" data-filter="all">${t('common.all')} (${this.reminders.length})</button>
        <button class="filter-btn ${this.filterStatus === 'completed' ? 'active' : ''}" data-filter="completed">${t('common.completed')} (${this.reminders.filter((reminder) => reminder.isCompleted).length})</button>
      </div>
      ${docs.length === 0
        ? `<div class="empty-state card">
            ${icon('bell', { size: 48, strokeWidth: 1.5, attrs: 'opacity="0.4"' })}
            <p>${this.filterStatus === 'open' ? t('reminder.emptyOpen') : this.filterStatus === 'completed' ? t('reminder.emptyCompleted') : t('reminder.emptyAll')}</p>
            ${this.filterStatus !== 'completed' ? `<button class="btn btn-primary" data-action="add">${t('reminder.add')}</button>` : ''}
          </div>`
        : `<div class="card table-wrapper">
            <table>
              <thead>
                <tr><th>${t('common.title')}</th><th>${t('common.property')}</th><th>${t('common.deadline')}</th><th>${t('common.status')}</th><th>${t('common.actions')}</th></tr>
              </thead>
              <tbody>
                ${docs.map((reminder) => {
                  let statusClass = 'badge-success';
                  let statusText = t('common.open');
                  let statusExtra = '';
                  if (reminder.isCompleted) {
                    statusClass = 'badge-muted';
                    statusText = t('common.completed');
                  } else if (isOverdue(reminder.dueDate)) {
                    statusClass = 'badge-danger';
                    statusText = t('common.overdue');
                    statusExtra = `<div class="text-sm text-danger">${t('common.daysAgo', { count: Math.abs(getDaysUntil(reminder.dueDate)) })}</div>`;
                  } else if (isDueSoon(reminder.dueDate)) {
                    statusClass = 'badge-warning';
                    statusText = t('common.soon');
                    statusExtra = `<div class="text-sm text-warning">${t('common.daysRemaining', { count: getDaysUntil(reminder.dueDate) })}</div>`;
                  }
                  const recurrenceLabel = formatRecurrence(reminder.recurrenceIntervalValue, reminder.recurrenceUnit);
                  return `
                    <tr class="${reminder.isCompleted ? 'row-completed' : ''}">
                      <td>
                        <div class="reminder-title">${esc(reminder.title)}</div>
                        ${recurrenceLabel ? `<div class="text-sm text-muted">${icon('repeat', { size: 11 })} ${recurrenceLabel}</div>` : ''}
                      </td>
                      <td>${reminder.propertyAddress ? `<span class="linked-to linked-to--property">${icon('home', { size: 12 })}${esc(reminder.propertyAddress)}</span>` : `<span class="text-muted">${t('common.none')}</span>`}</td>
                      <td><div>${formatDate(reminder.dueDate)}</div>${statusExtra}</td>
                      <td><span class="badge ${statusClass}">${statusText}</span></td>
                      <td>
                        <div class="card-actions">
                          ${!reminder.isCompleted ? `<button class="btn btn-outline btn-sm" data-action="complete" data-id="${reminder.id}">${icon('check', { size: 12, strokeWidth: 2.5 })}${t('reminder.complete')}</button>` : ''}
                          <button class="btn btn-danger btn-sm" data-action="delete" data-id="${reminder.id}">${t('common.delete')}</button>
                        </div>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>`}
    `;

    this.querySelectorAll('[data-action="add"]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('reminder-add', { bubbles: true }))));
    this.querySelectorAll<HTMLElement>('[data-action="complete"]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('reminder-complete', { bubbles: true, detail: { id: button.dataset.id } }))));
    this.querySelectorAll<HTMLElement>('[data-action="delete"]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('reminder-delete', { bubbles: true, detail: { id: button.dataset.id } }))));
    this.querySelectorAll<HTMLElement>('.filter-btn').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('reminder-filter-change', { bubbles: true, detail: { filterStatus: button.dataset.filter } }))));
  }
}

if (!customElements.get('rc-reminder-list-view')) {
  customElements.define('rc-reminder-list-view', RcReminderListView);
}
